import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { taskAPI, clientAPI } from '../../utils/crmApi';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  UserGroupIcon, 
  ClipboardDocumentListIcon, 
  ChatBubbleLeftRightIcon, 
  CalendarIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  ArrowPathIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ConsultantMode = () => {
  const { user, currentAgency } = useCRM();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todayTasks, setTodayTasks] = useState([]);
  const [clientAlerts, setClientAlerts] = useState([]);
  const [activeClientsCount, setActiveClientsCount] = useState(0);

  const primaryColor = branding?.primaryColor || '#3B82F6';
  const secondaryColor = branding?.secondaryColor || '#10B981';
  const accentColor = branding?.accentColor || '#F59E0B';

  const quickActions = [
    {
      title: 'New Client',
      description: 'Add a new relocation client',
      icon: UserGroupIcon,
      action: 'add-client',
      onClick: () => navigate('/clients/new')
    },
    {
      title: 'Follow Up',
      description: 'Check clients needing attention',
      icon: ChatBubbleLeftRightIcon,
      action: 'follow-up',
      onClick: () => navigate('/clients')
    },
    {
      title: 'Schedule Meeting',
      description: 'Book client appointment',
      icon: CalendarIcon,
      action: 'schedule',
      onClick: () => navigate('/calendar')
    },
    {
      title: 'AI Assistant',
      description: 'Get help with next steps',
      icon: SparklesIcon,
      action: 'ai-help',
      onClick: () => navigate('/ai-copilot')
    }
  ];

  useEffect(() => {
    if (user?._id && currentAgency?._id) {
      loadConsultantData();
    }
  }, [user?._id, currentAgency?._id]);

  const loadConsultantData = async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const userId = user._id || user.id;
      
      // Fetch tasks assigned to this consultant
      const tasksResponse = await taskAPI.getAll({
        assignedTo: userId,
        limit: 100
      });
      
      // Fetch clients assigned to this consultant
      const clientsResponse = await clientAPI.getAll({
        ownerUserId: userId,
        limit: 100
      });

      const tasks = tasksResponse.data?.data || [];
      const clients = clientsResponse.data?.data || [];

      // Filter today's tasks
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayTasksList = tasks
        .filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          return dueDate >= today && dueDate < tomorrow && task.status !== 'completed';
        })
        .map(task => ({
          id: task._id,
          client: task.clientId?.name || 'Unknown Client',
          task: task.title,
          priority: task.priority || 'medium',
          due: task.dueDate ? new Date(task.dueDate).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : 'No due date',
          clientId: task.clientId?._id || task.clientId
        }))
        .sort((a, b) => {
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
        })
        .slice(0, 10); // Limit to 10 tasks

      setTodayTasks(todayTasksList);

      // Calculate client alerts
      const alerts = [];
      
      // Overdue tasks
      const overdueTasks = tasks.filter(task => {
        if (!task.dueDate || task.status === 'completed') return false;
        return new Date(task.dueDate) < today;
      });

      overdueTasks.forEach(task => {
        const clientName = task.clientId ? (`${task.clientId.firstName || ''} ${task.clientId.lastName || ''}`.trim() || task.clientId.name || 'Unknown Client') : 'Unknown Client';
        const clientId = task.clientId?._id || task.clientId;
        alerts.push({
          id: `overdue-${task._id}`,
          client: clientName,
          issue: `Overdue task: ${task.title}`,
          severity: 'urgent',
          action: 'View task',
          clientId: clientId,
          taskId: task._id
        });
      });

      // Clients with no recent activity (no tasks updated in last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      clients.forEach(client => {
        const clientTasks = tasks.filter(t => {
          const tClientId = t.clientId?._id || t.clientId;
          return tClientId && String(tClientId) === String(client._id);
        });
        
        const hasRecentActivity = clientTasks.some(task => {
          const updatedAt = task.updatedAt ? new Date(task.updatedAt) : null;
          return updatedAt && updatedAt > sevenDaysAgo;
        });

        if (!hasRecentActivity && clientTasks.length > 0) {
          alerts.push({
            id: `low-engagement-${client._id}`,
            client: `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || client.email,
            issue: 'Low engagement - no recent activity',
            severity: 'warning',
            action: 'Check in',
            clientId: client._id
          });
        }
      });

      // Sort alerts by severity
      const severityOrder = { urgent: 0, warning: 1, info: 2 };
      alerts.sort((a, b) => (severityOrder[a.severity] || 99) - (severityOrder[b.severity] || 99));
      
      setClientAlerts(alerts.slice(0, 10)); // Limit to 10 alerts

      // Count active clients
      const activeClients = clients.filter(client => 
        client.status === 'active' || client.lifecycleStatus === 'active'
      );
      setActiveClientsCount(activeClients.length);

      if (showRefreshToast) {
        toast.success('Data refreshed');
      }

    } catch (error) {
      console.error('Error loading consultant data:', error);
      toast.error('Failed to load consultant data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadConsultantData(true);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default: return 'bg-muted text-foreground';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleTaskClick = (task) => {
    if (task.clientId) {
      navigate(`/clients/${task.clientId}`);
    }
  };

  const handleAlertClick = (alert) => {
    if (alert.taskId && alert.clientId) {
      navigate(`/clients/${alert.clientId}?taskId=${alert.taskId}`);
    } else if (alert.clientId) {
      navigate(`/clients/${alert.clientId}`);
    }
  };

  const displayName = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Consultant';

  // Convert hex to RGB for gradient
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 59, g: 130, b: 246 };
  };

  const primaryRgb = hexToRgb(primaryColor);
  const secondaryRgb = hexToRgb(secondaryColor);

  return (
    <div className="space-y-6">
      {/* Welcome Header with Branding */}
      <Card 
        className="border-0 text-white overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
        }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-bold">{getGreeting()}, {displayName}! 👋</h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                  className="text-white hover:bg-white/20 hover:text-white"
                >
                  <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <p className="text-white/90">Here's what needs your attention today</p>
            </div>
            <div className="text-right ml-6">
              <div className="text-3xl font-bold">{activeClientsCount}</div>
              <div className="text-sm text-white/80">Active Clients</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions with Branding */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <LightBulbIcon className="h-5 w-5 mr-2" style={{ color: accentColor }} />
            Quick Actions
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            const actionColors = [
              { bg: primaryColor, hover: secondaryColor },
              { bg: secondaryColor, hover: primaryColor },
              { bg: accentColor, hover: primaryColor },
              { bg: '#8B5CF6', hover: primaryColor }
            ];
            const color = actionColors[index % actionColors.length];
            
            return (
              <Button
                key={index}
                type="button"
                variant="outline"
                className="h-24 flex-col space-y-2 hover:shadow-lg transition-all duration-200 border-2 hover:border-opacity-50 group"
                onClick={action.onClick}
                style={{
                  borderColor: `${color.bg}40`
                }}
              >
                <div 
                  className="p-2 rounded-lg transition-colors duration-200 group-hover:scale-110"
                  style={{ 
                    backgroundColor: color.bg,
                  }}
                >
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-center">
                  <div className="font-medium text-sm">{action.title}</div>
                  <div className="text-xs text-muted-foreground">{action.description}</div>
                </div>
              </Button>
            );
          })}
        </div>
      </Card>

      {/* Today's Focus */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Tasks */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center">
              <ClipboardDocumentListIcon 
                className="h-5 w-5 mr-2" 
                style={{ color: primaryColor }} 
              />
              Today's Tasks
            </h2>
            {todayTasks.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/tasks')}
                className="text-xs"
              >
                View All
                <ArrowRightIcon className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading tasks...</div>
          ) : todayTasks.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-muted-foreground mb-2 opacity-50" />
              <p className="text-muted-foreground">No tasks due today</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/tasks')}
              >
                View All Tasks
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {todayTasks.map((task) => (
                <div 
                  key={task.id} 
                  className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 cursor-pointer transition-all duration-200 hover:shadow-sm group"
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{task.client}</div>
                    <div className="text-sm text-muted-foreground truncate">{task.task}</div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <Badge className={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Badge>
                    <div className="text-sm text-muted-foreground whitespace-nowrap">{task.due}</div>
                    <ArrowRightIcon className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Client Alerts */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center">
              <QuestionMarkCircleIcon 
                className="h-5 w-5 mr-2" 
                style={{ color: '#EF4444' }} 
              />
              Client Alerts
            </h2>
            {clientAlerts.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/clients')}
                className="text-xs"
              >
                View All
                <ArrowRightIcon className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading alerts...</div>
          ) : clientAlerts.length === 0 ? (
            <div className="text-center py-8">
              <QuestionMarkCircleIcon className="h-12 w-12 mx-auto text-muted-foreground mb-2 opacity-50" />
              <p className="text-muted-foreground">No alerts at this time</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/clients')}
              >
                View All Clients
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {clientAlerts.map((alert) => (
                <div 
                  key={alert.id} 
                  className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 cursor-pointer transition-all duration-200 hover:shadow-sm group"
                  onClick={() => handleAlertClick(alert)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{alert.client}</div>
                    <div className="text-sm text-muted-foreground truncate">{alert.issue}</div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <Badge className={getPriorityColor(alert.severity)}>
                      {alert.severity}
                    </Badge>
                    <Button 
                      type="button"
                      size="sm" 
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAlertClick(alert);
                      }}
                      className="whitespace-nowrap"
                    >
                      {alert.action}
                    </Button>
                    <ArrowRightIcon className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* AI Assistant with Branding */}
      <Card 
        className="p-6 border-2 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}08 100%)`,
          borderColor: `${primaryColor}30`
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: primaryColor }}
            >
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">AI Assistant</h3>
              <p className="text-sm text-muted-foreground">Get personalized recommendations</p>
            </div>
          </div>
          <Button 
            type="button"
            onClick={() => navigate('/ai-copilot')}
            style={{
              backgroundColor: primaryColor,
            }}
            className="text-white hover:opacity-90 transition-opacity"
          >
            Ask AI
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ConsultantMode;
